import downloadCenter from '@/utils/downloadCenter'
import { getAppUiText } from '@/utils/language/common'
import pdfjsLib from '@/utils/pdfjs'
import JSZip from 'jszip'

const DOWNLOAD_BUNDLE_THRESHOLD = 10
const DOWNLOAD_PDF_A4_SHORT_EDGE = 595.276
const DOWNLOAD_PDF_A4_LONG_EDGE = 841.89
const DOWNLOAD_PDF_A4_RENDER_SCALE = 2
const DOWNLOAD_IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|webp|bmp|gif|avif)$/i
const DOWNLOAD_PDF_EXTENSION_PATTERN = /\.pdf$/i
const DOWNLOAD_ZIP_EXTENSION_PATTERN = /\.zip$/i

export default {
  computed: {
    paperDownloadTasks: {
      get() {
        return downloadCenter.state.tasks
      },
      set(value) {
        downloadCenter.state.tasks = Array.isArray(value) ? value : []
      }
    },
    paperDownloadListVisible: {
      get() {
        return downloadCenter.state.visible
      },
      set(value) {
        downloadCenter.state.visible = !!value
      }
    },
    paperDownloadPanelCollapsed: {
      get() {
        return downloadCenter.state.collapsed
      },
      set(value) {
        downloadCenter.state.collapsed = !!value
      }
    }
  },
  methods: {
    $canStartDownloadBatch(selectionCount, taskCount = selectionCount, options = {}) {
      const result = downloadCenter.validateStart(selectionCount, taskCount, undefined, options)
      if (!result.ok && this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning(result.message)
      }
      return result.ok
    },
    $getDownloadBatchTaskCount(selectionCount) {
      const count = Number(selectionCount) || 0
      return count > DOWNLOAD_BUNDLE_THRESHOLD ? 1 : count
    },
    $canStartFileDownloadBatch(selectionCount, options = {}) {
      return this.$canStartDownloadBatch(selectionCount, this.$getDownloadBatchTaskCount(selectionCount), options)
    },
    $shouldBundleDownloadItems(items) {
      return (Array.isArray(items) ? items.length : Number(items) || 0) > DOWNLOAD_BUNDLE_THRESHOLD
    },
    $getDownloadText(key, fallback) {
      const ui = this.ui || this.uiText || {}
      return ui[key] || fallback
    },
    $getDownloadRunningStatusText() {
      return this.$getDownloadText('downloading', '下载中')
    },
    $getAppDownloadText(key, fallback) {
      const text = this.$getDownloadText(key, '')
      if (text) {
        return text
      }
      const language = this.language || this.selectedLanguage
      const ui = getAppUiText(language)
      return ui[key] || fallback
    },
    $getLearningAnnotationsEmptyDownloadText() {
      return this.$getAppDownloadText('learningAnnotationsEmptyDownload', '\u6279\u6ce8\u4e3a\u7a7a\uff0c\u6682\u65f6\u4e0d\u652f\u6301\u4e0b\u8f7d')
    },
    $createLearningAnnotationsEmptyDownloadError() {
      const error = new Error(this.$getLearningAnnotationsEmptyDownloadText())
      error.isLearningAnnotationsEmptyDownload = true
      return error
    },
    $isLearningAnnotationsEmptyDownloadError(error) {
      return !!(error && error.isLearningAnnotationsEmptyDownload)
    },
    $notifyLearningAnnotationsEmptyDownload() {
      const message = this.$getLearningAnnotationsEmptyDownloadText()
      if (this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning(message)
        return
      }
      if (this.$message && typeof this.$message.info == 'function') {
        this.$message.info(message)
      }
    },
    $handleLearningAnnotationsEmptyDownloadTask(task, options = {}) {
      if (!options.silent) {
        this.$notifyLearningAnnotationsEmptyDownload()
      }
      if (!task) {
        return
      }
      const message = this.$getLearningAnnotationsEmptyDownloadText()
      this.$set(task, 'state', 'learning-empty-annotations')
      this.$set(task, 'statusText', message)
      if (!options.keepTask && !task.captureBlob) {
        if (typeof this.removePaperDownloadTask == 'function') {
          this.removePaperDownloadTask(task)
        } else {
          downloadCenter.removeTask(task)
        }
      }
    },
    $stripDownloadFileExtension(name) {
      return String(name || '').trim().replace(/\.(?=[^./\\]*[a-zA-Z])[^./\\]{1,16}$/i, '')
    },
    $getDownloadBaseName(name, fallbackName = 'download', sanitizer) {
      const fallback = fallbackName || 'download'
      const sanitize = sanitizer || this.sanitizeFileName || this.sanitizeRecentFileName
      const normalize = value => {
        const cleanName = this.$stripDownloadFileExtension(value)
        return typeof sanitize == 'function' ? sanitize.call(this, cleanName) : String(cleanName || '').trim()
      }
      return normalize(name || fallback) || normalize(fallback) || 'download'
    },
    $getDownloadFileName(name, extension, fallbackName = 'download', sanitizer) {
      const safeExtension = String(extension || '').replace(/^\.+/, '').trim()
      const baseName = this.$getDownloadBaseName(name, fallbackName, sanitizer)
      return safeExtension ? `${baseName}.${safeExtension}` : baseName
    },
    $getDownloadItemPageCount(item) {
      const source = item && typeof item == 'object' ? item : {}
      const candidates = [
        source.pageCount,
        source.pages,
        source.pageNum,
        source.imagePageCount,
        source.totalPage,
        source.childSize
      ]
      if (Array.isArray(source.imagePages)) {
        candidates.push(source.imagePages.length)
      }
      for (const value of candidates) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            return value.length
          }
          continue
        }
        const count = Number(value)
        if (Number.isFinite(count) && count > 0) {
          return count
        }
      }
      return 0
    },
    $getPaperDownloadPngExtension(pageCount) {
      return Number(pageCount) == 1 ? 'png' : 'zip'
    },
    $getPaperDownloadTaskExtension(item, format) {
      if (format != 'png') {
        return 'pdf'
      }
      return this.$getPaperDownloadPngExtension(this.$getDownloadItemPageCount(item))
    },
    $updatePaperDownloadTaskFileNameForPages(task, format, pages) {
      if (!task) {
        return
      }
      const safeFormat = format == 'png' ? 'png' : (format == 'pdf' ? 'pdf' : '')
      if (!safeFormat) {
        return
      }
      const pageCount = Array.isArray(pages) ? pages.length : Number(pages) || 0
      const baseName = this.$getDownloadBaseName(task.name || task.fileName, this.$getDownloadText('download', 'download'))
      const extension = safeFormat == 'pdf' ? 'pdf' : this.$getPaperDownloadPngExtension(pageCount)
      const fileName = `${baseName}.${extension}`
      if (task.fileName == fileName) {
        return
      }
      if (typeof this.updatePaperDownloadTask == 'function') {
        this.updatePaperDownloadTask(task, { fileName })
        return
      }
      this.$set(task, 'fileName', fileName)
    },
    $getDownloadBundleBaseName(items, options = {}) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      const first = list[0] || {}
      const fallback = options.fallbackName || this.$getDownloadText('download', 'download')
      const rawName = typeof options.getItemName == 'function'
        ? options.getItemName(first)
        : (first && first.name ? first.name : fallback)
      const sanitizer = options.sanitizeFileName || this.sanitizeFileName || this.sanitizeRecentFileName
      return this.$getDownloadBaseName(rawName || fallback, fallback, sanitizer)
    },
    $getDownloadBundleFileName(items, options = {}) {
      return `${this.$getDownloadBundleBaseName(items, options)}等.zip`
    },
    $createDownloadBundleTask(items, format, password = '', options = {}) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      const baseName = this.$getDownloadBundleBaseName(list, options)
      const fileName = `${baseName}等.zip`
      return {
        id: `${options.idPrefix || 'download-bundle'}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: options.kind || 'download-bundle',
        name: `${baseName}等`,
        fileName,
        bundleFileName: fileName,
        bundleName: `${baseName}等`,
        sourceItems: list.slice(),
        format,
        password,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.$getDownloadRunningStatusText(),
        bundleItems: [],
        bundleCompletedCount: 0,
        bundleFailedCount: 0,
        bundleTotalCount: list.length,
        bundleProgressCurrent: 0,
        bundleProgressTotal: list.length
      }
    },
    $captureDownloadBlob(task, blob, fileName) {
      if (task && typeof this.ensurePaperDownloadTaskActive == 'function') {
        this.ensurePaperDownloadTaskActive(task)
      }
      if (!task || !task.captureBlob) {
        return false
      }
      this.$set(task, 'capturedBlob', blob)
      this.$set(task, 'capturedFileName', fileName || task.fileName || '')
      if (typeof this.updatePaperDownloadTask == 'function') {
        this.updatePaperDownloadTask(task, { progress: Math.max(task.progress || 0, 99) })
      }
      return true
    },
    $getDownloadBundleItemProgress(index, total, percent) {
      const safeTotal = Math.max(Number(total) || 1, 1)
      const safeIndex = Math.max(0, Math.min(Number(index) || 0, safeTotal - 1))
      const safePercent = Math.max(0, Math.min(Number(percent) || 0, 100))
      return Math.min(100, Math.round(((safeIndex + safePercent / 100) / safeTotal) * 100))
    },
    $getDownloadBundleStatusText(current, total, statusText) {
      const safeTotal = Math.max(Number(total) || 0, 0)
      if (safeTotal <= 0) {
        return statusText || this.$getDownloadRunningStatusText()
      }
      const safeCurrent = Math.max(0, Math.min(Number(current) || 0, safeTotal))
      return `${statusText || this.$getDownloadRunningStatusText()}（${safeCurrent}/${safeTotal}）`
    },
    $getDownloadBundleDoneStatusText(current, total) {
      return this.$getDownloadBundleStatusText(current, total, this.$getDownloadText('downloadDone', '下载完成'))
    },
    $getDownloadBundleItemStatusText(progressContext = {}) {
      const total = Number(progressContext.total) || 0
      const index = Math.max(0, Number(progressContext.index) || 0)
      return this.$getDownloadBundleStatusText(index + 1, total)
    },
    $isDownloadBundleTask(task) {
      return !!(task && (Array.isArray(task.sourceItems) || String(task.kind || '').includes('bundle')))
    },
    $getDownloadBundleItemDisplayName(item, itemTask, index, options = {}) {
      if (typeof options.getItemName == 'function') {
        const name = options.getItemName(item)
        if (name) {
          return String(name).trim()
        }
      }
      const candidates = [
        item && item.name,
        item && item.title,
        item && item.fileName,
        item && item.filename,
        item && item.resourceName,
        item && item.paperName,
        itemTask && itemTask.name,
        itemTask && itemTask.fileName
      ]
      for (const value of candidates) {
        const text = String(value || '').trim()
        if (text) {
          return text
        }
      }
      return `file-${index + 1}`
    },
    $notifyDownloadBundleItemFailure(itemName) {
      const name = String(itemName || '').trim() || this.$getDownloadText('download', 'download')
      const message = `${name} 下载失败，已跳过`
      if (this.$message && typeof this.$message.warning == 'function') {
        this.$message.warning(message)
        return
      }
      if (this.$message && typeof this.$message.info == 'function') {
        this.$message.info(message)
      }
    },
    $getDownloadBundleFailureReason(error) {
      const candidates = []
      if (typeof error == 'string') {
        candidates.push(error)
      }
      candidates.push(
        error && error.failureReason,
        error && error.errorMessage,
        error && error.message,
        error && error.statusText
      )
      for (const value of candidates) {
        const text = String(value || '').trim()
        if (text) {
          return text
        }
      }
      return this.$getDownloadText('downloadFailed', '下载失败')
    },
    $getDownloadBundleItemFailureStatusText(error, options = {}, context = {}) {
      if (typeof options.getItemFailureStatusText == 'function') {
        const customStatusText = String(options.getItemFailureStatusText(error, context) || '').trim()
        if (customStatusText) {
          return customStatusText
        }
      }
      return this.$getDownloadBundleRecordStatusText('error')
    },
    $shouldNotifyDownloadBundleItemFailure(error, options = {}, context = {}) {
      return typeof options.shouldNotifyItemFailure != 'function' || options.shouldNotifyItemFailure(error, context) !== false
    },
    $getDownloadBundleCountProgress(current, total) {
      const safeTotal = Math.max(Number(total) || 0, 0)
      if (safeTotal <= 0) {
        return 0
      }
      const safeCurrent = Math.max(0, Math.min(Number(current) || 0, safeTotal))
      return Math.min(100, Math.round((safeCurrent / safeTotal) * 100))
    },
    $setDownloadBundleCount(task, current, total) {
      if (!task) {
        return
      }
      const safeTotal = Math.max(Number(total) || 0, 0)
      const safeCurrent = Math.max(0, Math.min(Number(current) || 0, safeTotal || Number(current) || 0))
      this.$set(task, 'bundleProgressCurrent', safeCurrent)
      this.$set(task, 'bundleProgressTotal', safeTotal)
    },
    $getDownloadBundleRecordStatusText(state) {
      if (state == 'done') {
        return this.$getDownloadText('downloadDone', '下载完成')
      }
      if (state == 'error') {
        return this.$getDownloadText('downloadFailed', '下载失败')
      }
      if (state == 'pending') {
        return this.$getDownloadText('downloadPreparing', '准备下载')
      }
      return this.$getDownloadRunningStatusText()
    },
    $createDownloadBundleItemRecords(task, items, options = {}) {
      const list = Array.isArray(items) ? items : []
      return list.map((item, index) => ({
        id: `${task && task.id ? task.id : 'bundle'}-item-${index}`,
        index,
        name: this.$getDownloadBundleItemDisplayName(item, null, index, options),
        fileName: '',
        progress: 0,
        state: 'pending',
        statusText: this.$getDownloadBundleRecordStatusText('pending'),
        failureReason: ''
      }))
    },
    $updateDownloadBundleItemRecord(record, patch = {}) {
      if (!record || !patch) {
        return
      }
      Object.keys(patch).forEach(key => {
        this.$set(record, key, patch[key])
      })
    },
    $syncDownloadBundleItemRecordPatch(itemTask, patch) {
      const record = itemTask && itemTask.__downloadBundleItemRecord
      if (!record || !patch) {
        return
      }
      const recordPatch = {}
      if (Object.prototype.hasOwnProperty.call(patch, 'name') && patch.name) {
        recordPatch.name = patch.name
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'fileName') && patch.fileName) {
        recordPatch.fileName = patch.fileName
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'failureReason')) {
        recordPatch.failureReason = patch.failureReason ? String(patch.failureReason) : ''
      } else if (Object.prototype.hasOwnProperty.call(patch, 'state')) {
        if (patch.state == 'running' || patch.state == 'done') {
          recordPatch.failureReason = ''
        } else if (patch.state == 'error') {
          recordPatch.failureReason = patch.statusText ? String(patch.statusText) : ''
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'progress')) {
        recordPatch.progress = Math.max(0, Math.min(Math.round(Number(patch.progress) || 0), 100))
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'state')) {
        recordPatch.state = patch.state
        if (patch.state == 'done') {
          recordPatch.progress = 100
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'statusText') || Object.prototype.hasOwnProperty.call(patch, 'state')) {
        recordPatch.statusText = this.$getDownloadBundleRecordStatusText(recordPatch.state || patch.state || record.state)
      }
      this.$updateDownloadBundleItemRecord(record, recordPatch)
    },
    $refreshDownloadBundleCountsFromItems(task, statusText) {
      if (!task) {
        return { completed: 0, failed: 0, total: 0, progress: 0 }
      }
      const records = Array.isArray(task.bundleItems) ? task.bundleItems : []
      const total = Number(task.bundleTotalCount) || records.length || 0
      const completed = records.filter(item => item && item.state == 'done').length
      const failed = records.filter(item => item && item.state == 'error').length
      const progress = this.$getDownloadBundleCountProgress(completed, total)
      const isComplete = total > 0 && completed >= total && failed == 0
      const hasSettled = total > 0 && completed + failed >= total
      const nextState = isComplete ? 'done' : (hasSettled ? 'done' : (task.state || 'running'))
      const nextStatusText = statusText || this.$getDownloadBundleStatusText(
        completed,
        total,
        nextState == 'error' ? this.$getDownloadText('downloadFailed', '下载失败') : this.$getDownloadText('downloadDone', '下载完成')
      )
      this.$set(task, 'bundleCompletedCount', completed)
      this.$set(task, 'bundleFailedCount', failed)
      this.$set(task, 'bundleTotalCount', total)
      this.$set(task, 'bundleProgressCurrent', completed)
      this.$set(task, 'bundleProgressTotal', total)
      if (typeof this.updatePaperDownloadTask == 'function') {
        this.updatePaperDownloadTask(task, {
          progress,
          state: nextState,
          statusText: nextStatusText
        })
      }
      return { completed, failed, total, progress }
    },
    $registerDownloadBundleItemRetryHandler(task, options = {}) {
      if (!task) {
        return
      }
      const handlers = task.__downloadHandlers || {}
      downloadCenter.registerTaskHandlers(task, Object.assign({}, handlers, {
        retryBundleItem: (bundleTask, bundleItem) => {
          this.$retryDownloadBundleItemTask(bundleTask, bundleItem, options)
        }
      }))
    },
    $prepareDownloadBundleRetryItemTask(parentTask, itemTask, itemRecord) {
      const runtimeTask = Object.assign({}, itemTask || {})
      const preparedDetail = itemTask && itemTask.detail ? itemTask.detail : null
      const preparedPages = itemTask && Array.isArray(itemTask.pages) ? itemTask.pages.slice() : []
      let runtimeCancelled = !!runtimeTask.cancelled
      let runtimeAbortController = runtimeTask.abortController || null
      Object.defineProperty(runtimeTask, '__downloadBundleItemRecord', {
        value: itemRecord || null,
        configurable: true,
        enumerable: false,
        writable: true
      })
      Object.defineProperty(runtimeTask, 'cancelled', {
        configurable: true,
        enumerable: true,
        get: () => !!(runtimeCancelled || parentTask && parentTask.cancelled),
        set: value => {
          runtimeCancelled = !!value
        }
      })
      Object.defineProperty(runtimeTask, 'abortController', {
        configurable: true,
        enumerable: true,
        get: () => runtimeAbortController,
        set: value => {
          runtimeAbortController = value || null
          if (itemRecord) {
            this.$set(itemRecord, 'abortController', runtimeAbortController)
          }
          if (parentTask && parentTask.abortController !== runtimeAbortController) {
            this.$set(parentTask, 'abortController', runtimeAbortController)
          }
        }
      })
      runtimeTask.id = parentTask && parentTask.id ? parentTask.id : runtimeTask.id
      runtimeTask.captureBlob = true
      runtimeTask.capturedBlob = null
      runtimeTask.capturedFileName = ''
      runtimeTask.detail = preparedDetail
      runtimeTask.pages = preparedPages
      runtimeTask.pngFiles = []
      runtimeTask.pdfPages = []
      runtimeTask.pdfPageGroups = []
      runtimeTask.failedPageIndex = 0
      runtimeTask.pdfDocument = null
      runtimeTask.pdfPageCount = 0
      runtimeTask.openUrl = ''
      runtimeTask.state = 'running'
      return runtimeTask
    },
    async $retryDownloadBundleItemTask(parentTask, bundleItem, options = {}) {
      if (!parentTask || !bundleItem || bundleItem.state == 'running') {
        return
      }
      const records = Array.isArray(parentTask.bundleItems) ? parentTask.bundleItems : []
      const index = Math.max(0, Number(bundleItem.index) || 0)
      const item = Array.isArray(parentTask.sourceItems) ? parentTask.sourceItems[index] : null
      if (!item || typeof options.createItemTask != 'function' || typeof options.runItemTask != 'function') {
        this.$updateDownloadBundleItemRecord(bundleItem, {
          state: 'error',
          statusText: this.$getDownloadBundleRecordStatusText('error'),
          failureReason: this.$getDownloadBundleFailureReason('Download unavailable')
        })
        this.$refreshDownloadBundleCountsFromItems(parentTask)
        return
      }
      const selectedFormat = options.format || parentTask.format
      const password = options.password === undefined ? (parentTask.password || '') : options.password
      let itemFormat = selectedFormat
      let runtimeTask = null
      try {
        if (typeof this.ensurePaperDownloadTaskActive == 'function') {
          this.ensurePaperDownloadTaskActive(parentTask)
        }
        itemFormat = typeof options.getItemFormat == 'function' ? options.getItemFormat(item, selectedFormat) : selectedFormat
        const itemTask = options.createItemTask(item, itemFormat, password)
        const itemName = this.$getDownloadBundleItemDisplayName(item, itemTask, index, options)
        this.$updateDownloadBundleItemRecord(bundleItem, {
          name: itemName,
          fileName: itemTask && itemTask.fileName ? itemTask.fileName : bundleItem.fileName,
          progress: 0,
          state: 'running',
          statusText: this.$getDownloadBundleRecordStatusText('running'),
          failureReason: ''
        })
        const completedBeforeRetry = records.filter(record => record && record.state == 'done').length
        const bundleTotal = Number(parentTask.bundleTotalCount) || records.length
        this.updatePaperDownloadTask(parentTask, {
          state: 'running',
          progress: this.$getDownloadBundleCountProgress(completedBeforeRetry, bundleTotal),
          statusText: this.$getDownloadBundleStatusText(completedBeforeRetry, bundleTotal)
        })
        runtimeTask = this.$prepareDownloadBundleRetryItemTask(parentTask, itemTask, bundleItem)
        await options.runItemTask(runtimeTask, item, itemFormat)
        if (parentTask.cancelled || runtimeTask.cancelled) {
          const cancelError = new Error('Download cancelled')
          cancelError.isPaperDownloadCancelled = true
          throw cancelError
        }
        if (runtimeTask.state == 'error') {
          throw new Error(runtimeTask.statusText || this.$getDownloadText('downloadFailed', '下载失败'))
        }
        if (!runtimeTask.capturedBlob) {
          throw new Error(this.$getDownloadText('downloadFailed', '下载失败'))
        }
        const retryEntry = await this.$prepareDownloadBundleEntry(runtimeTask, itemFormat, selectedFormat, index)
        if (typeof this.ensurePaperDownloadTaskActive == 'function') {
          this.ensurePaperDownloadTaskActive(parentTask)
        }
        const saveTask = Object.assign({}, runtimeTask, {
          captureBlob: false,
          fileName: retryEntry.fileName,
          name: this.$stripDownloadFileExtension(retryEntry.fileName),
          openUrl: '',
          progress: Math.max(runtimeTask.progress || 0, 99)
        })
        this.savePaperDownloadBlob(saveTask, retryEntry.blob)
        this.$updateDownloadBundleItemRecord(bundleItem, {
          fileName: retryEntry.fileName,
          progress: 100,
          state: 'done',
          statusText: this.$getDownloadBundleRecordStatusText('done'),
          failureReason: '',
          abortController: null
        })
        if (parentTask && parentTask.abortController === runtimeTask.abortController) {
          this.$set(parentTask, 'abortController', null)
        }
        this.$refreshDownloadBundleCountsFromItems(parentTask)
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const failureContext = { item, itemTask: runtimeTask, itemRecord: bundleItem, index }
        this.$updateDownloadBundleItemRecord(bundleItem, {
          progress: Math.max(0, Math.min(Math.round(Number(bundleItem.progress) || 0), 99)),
          state: 'error',
          statusText: this.$getDownloadBundleItemFailureStatusText(error, options, failureContext),
          failureReason: this.$getDownloadBundleFailureReason(error),
          abortController: null
        })
        if (parentTask && runtimeTask && parentTask.abortController === runtimeTask.abortController) {
          this.$set(parentTask, 'abortController', null)
        }
        if (this.$shouldNotifyDownloadBundleItemFailure(error, options, failureContext)) {
          this.$notifyDownloadBundleItemFailure(bundleItem.name)
        }
        this.$refreshDownloadBundleCountsFromItems(parentTask)
      } finally {
        if (records.length > 0) {
          this.$set(parentTask, 'bundleItems', records.slice())
        }
      }
    },
    $getDownloadBlobMimeType(blob) {
      return String(blob && blob.type || '').toLowerCase()
    },
    $isDownloadPdfA4Enabled() {
      return this.$options.downloadPdfPageSize == 'a4'
    },
    $getDownloadPdfPageLayout(page) {
      const imageWidth = Math.max(1, Math.round(Number(page && page.width) || 1))
      const imageHeight = Math.max(1, Math.round(Number(page && page.height) || 1))
      if (!this.$isDownloadPdfA4Enabled()) {
        return {
          pageWidth: imageWidth,
          pageHeight: imageHeight,
          imageWidth,
          imageHeight,
          drawWidth: imageWidth,
          drawHeight: imageHeight,
          offsetX: 0,
          offsetY: 0
        }
      }
      const landscape = imageWidth > imageHeight
      const pageWidth = landscape ? DOWNLOAD_PDF_A4_LONG_EDGE : DOWNLOAD_PDF_A4_SHORT_EDGE
      const pageHeight = landscape ? DOWNLOAD_PDF_A4_SHORT_EDGE : DOWNLOAD_PDF_A4_LONG_EDGE
      const scale = Math.min(pageWidth / imageWidth, pageHeight / imageHeight)
      const drawWidth = Number((imageWidth * scale).toFixed(3))
      const drawHeight = Number((imageHeight * scale).toFixed(3))
      return {
        pageWidth,
        pageHeight,
        imageWidth,
        imageHeight,
        drawWidth,
        drawHeight,
        offsetX: Number(((pageWidth - drawWidth) / 2).toFixed(3)),
        offsetY: Number(((pageHeight - drawHeight) / 2).toFixed(3))
      }
    },
    $isDownloadPdfA4Page(width, height) {
      const shortEdge = Math.min(Number(width) || 0, Number(height) || 0)
      const longEdge = Math.max(Number(width) || 0, Number(height) || 0)
      return Math.abs(shortEdge - DOWNLOAD_PDF_A4_SHORT_EDGE) <= 1
        && Math.abs(longEdge - DOWNLOAD_PDF_A4_LONG_EDGE) <= 1
    },
    async $convertDownloadPdfBlobToA4(blob) {
      const normalizedBlob = this.$normalizeDownloadPdfBlob(blob)
      if (!normalizedBlob || !this.$isDownloadPdfA4Enabled() || typeof document === 'undefined') {
        return normalizedBlob
      }
      let pdfDocument = null
      try {
        const bytes = new Uint8Array(await this.$blobToArrayBuffer(normalizedBlob))
        pdfDocument = await pdfjsLib.getDocument({ data: bytes }).promise
        const sourcePages = []
        let alreadyA4 = pdfDocument.numPages > 0
        for (let pageNo = 1; pageNo <= pdfDocument.numPages; pageNo++) {
          const pdfPage = await pdfDocument.getPage(pageNo)
          const viewport = pdfPage.getViewport({ scale: 1 })
          sourcePages.push({ pdfPage, viewport })
          if (!this.$isDownloadPdfA4Page(viewport.width, viewport.height)) {
            alreadyA4 = false
          }
        }
        if (alreadyA4) {
          return normalizedBlob
        }
        const imagePages = []
        for (const sourcePage of sourcePages) {
          const sourceWidth = Math.max(1, Number(sourcePage.viewport.width) || 1)
          const sourceHeight = Math.max(1, Number(sourcePage.viewport.height) || 1)
          const landscape = sourceWidth > sourceHeight
          const targetWidth = (landscape ? DOWNLOAD_PDF_A4_LONG_EDGE : DOWNLOAD_PDF_A4_SHORT_EDGE) * DOWNLOAD_PDF_A4_RENDER_SCALE
          const targetHeight = (landscape ? DOWNLOAD_PDF_A4_SHORT_EDGE : DOWNLOAD_PDF_A4_LONG_EDGE) * DOWNLOAD_PDF_A4_RENDER_SCALE
          const renderScale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight)
          const viewport = sourcePage.pdfPage.getViewport({ scale: renderScale })
          const canvas = document.createElement('canvas')
          canvas.width = Math.max(1, Math.ceil(viewport.width))
          canvas.height = Math.max(1, Math.ceil(viewport.height))
          try {
            const context = canvas.getContext('2d')
            if (!context) {
              throw new Error('PDF conversion unavailable')
            }
            context.fillStyle = '#fff'
            context.fillRect(0, 0, canvas.width, canvas.height)
            await sourcePage.pdfPage.render({ canvasContext: context, viewport, background: 'rgb(255,255,255)' }).promise
            const jpegBlob = await new Promise((resolve, reject) => {
              canvas.toBlob(value => value ? resolve(value) : reject(new Error('PDF conversion unavailable')), 'image/jpeg', 1)
            })
            imagePages.push({
              width: canvas.width,
              height: canvas.height,
              data: new Uint8Array(await this.$blobToArrayBuffer(jpegBlob))
            })
          } finally {
            canvas.width = 0
            canvas.height = 0
          }
        }
        return this.$buildDownloadPdfBlob(imagePages)
      } catch (error) {
        return normalizedBlob
      } finally {
        if (pdfDocument && typeof pdfDocument.destroy == 'function') {
          try {
            await pdfDocument.destroy()
          } catch (error) {
            // Cleanup must not replace the original PDF fallback.
          }
        }
      }
    },
    $isDownloadPdfBlob(fileName, blob) {
      const mimeType = this.$getDownloadBlobMimeType(blob)
      if (mimeType.startsWith('image/') || mimeType.includes('zip') || mimeType.includes('compressed')) {
        return false
      }
      const name = String(fileName || '').split(/[?#]/)[0].toLowerCase()
      return mimeType.includes('pdf') || DOWNLOAD_PDF_EXTENSION_PATTERN.test(name)
    },
    $isDownloadZipBlob(fileName, blob) {
      const mimeType = this.$getDownloadBlobMimeType(blob)
      const name = String(fileName || '').split(/[?#]/)[0].toLowerCase()
      return mimeType.includes('zip') || DOWNLOAD_ZIP_EXTENSION_PATTERN.test(name)
    },
    $isDownloadImageBlob(fileName, blob) {
      const mimeType = this.$getDownloadBlobMimeType(blob)
      if (mimeType.includes('pdf') || mimeType.includes('zip') || mimeType.includes('compressed')) {
        return false
      }
      const name = String(fileName || '').split(/[?#]/)[0].toLowerCase()
      return mimeType.startsWith('image/') || DOWNLOAD_IMAGE_EXTENSION_PATTERN.test(name)
    },
    $getDownloadImageMimeType(fileName, blob) {
      const mimeType = this.$getDownloadBlobMimeType(blob)
      if (mimeType.startsWith('image/')) {
        return mimeType
      }
      const name = String(fileName || '').split(/[?#]/)[0].toLowerCase()
      if (/\.(jpe?g)$/i.test(name)) {
        return 'image/jpeg'
      }
      if (/\.webp$/i.test(name)) {
        return 'image/webp'
      }
      if (/\.bmp$/i.test(name)) {
        return 'image/bmp'
      }
      if (/\.gif$/i.test(name)) {
        return 'image/gif'
      }
      if (/\.avif$/i.test(name)) {
        return 'image/avif'
      }
      return 'image/png'
    },
    $normalizeDownloadImageBlob(blob, fileName) {
      if (!blob || this.$getDownloadBlobMimeType(blob).startsWith('image/')) {
        return blob
      }
      return new Blob([blob], { type: this.$getDownloadImageMimeType(fileName, blob) })
    },
    $normalizeDownloadPdfBlob(blob) {
      if (!blob || this.$getDownloadBlobMimeType(blob).includes('pdf')) {
        return blob
      }
      return new Blob([blob], { type: 'application/pdf' })
    },
    $blobToArrayBuffer(blob) {
      if (blob && typeof blob.arrayBuffer == 'function') {
        return blob.arrayBuffer()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error || new Error('Blob read failed'))
        reader.readAsArrayBuffer(blob)
      })
    },
    async $getDownloadBlobSignature(blob) {
      if (!blob || typeof blob.slice != 'function') {
        return ''
      }
      const bytes = new Uint8Array(await this.$blobToArrayBuffer(blob.slice(0, 16)))
      if (bytes.length >= 4 && bytes[0] == 0x25 && bytes[1] == 0x50 && bytes[2] == 0x44 && bytes[3] == 0x46) {
        return 'pdf'
      }
      if (bytes.length >= 4 && bytes[0] == 0x50 && bytes[1] == 0x4b && bytes[2] == 0x03 && bytes[3] == 0x04) {
        return 'zip'
      }
      if (bytes.length >= 8 && bytes[0] == 0x89 && bytes[1] == 0x50 && bytes[2] == 0x4e && bytes[3] == 0x47 && bytes[4] == 0x0d && bytes[5] == 0x0a && bytes[6] == 0x1a && bytes[7] == 0x0a) {
        return 'image/png'
      }
      if (bytes.length >= 3 && bytes[0] == 0xff && bytes[1] == 0xd8 && bytes[2] == 0xff) {
        return 'image/jpeg'
      }
      if (bytes.length >= 6 && bytes[0] == 0x47 && bytes[1] == 0x49 && bytes[2] == 0x46 && bytes[3] == 0x38) {
        return 'image/gif'
      }
      if (bytes.length >= 2 && bytes[0] == 0x42 && bytes[1] == 0x4d) {
        return 'image/bmp'
      }
      if (bytes.length >= 12 && bytes[0] == 0x52 && bytes[1] == 0x49 && bytes[2] == 0x46 && bytes[3] == 0x46 && bytes[8] == 0x57 && bytes[9] == 0x45 && bytes[10] == 0x42 && bytes[11] == 0x50) {
        return 'image/webp'
      }
      return ''
    },
    $isDownloadImageSignature(signature) {
      return String(signature || '').startsWith('image/')
    },
    $downloadImageBlobToPdfPage(blob, fileName) {
      return new Promise((resolve, reject) => {
        const sourceBlob = this.$normalizeDownloadImageBlob(blob, fileName)
        const objectUrl = URL.createObjectURL(sourceBlob)
        const image = new Image()
        let settled = false
        const fail = error => {
          if (settled) {
            return
          }
          settled = true
          URL.revokeObjectURL(objectUrl)
          reject(error instanceof Error ? error : new Error('Image convert failed'))
        }
        image.onload = () => {
          try {
            const width = Math.max(1, image.naturalWidth || image.width || 1)
            const height = Math.max(1, image.naturalHeight || image.height || 1)
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const context = canvas.getContext('2d')
            if (!context) {
              fail(new Error('Image convert failed'))
              return
            }
            if ('imageSmoothingEnabled' in context) {
              context.imageSmoothingEnabled = true
              if ('imageSmoothingQuality' in context) {
                context.imageSmoothingQuality = 'high'
              }
            }
            context.fillStyle = '#fff'
            context.fillRect(0, 0, width, height)
            context.drawImage(image, 0, 0, width, height)
            canvas.toBlob(async jpegBlob => {
              try {
                if (!jpegBlob) {
                  fail(new Error('Image convert failed'))
                  return
                }
                const arrayBuffer = await this.$blobToArrayBuffer(jpegBlob)
                canvas.width = 0
                canvas.height = 0
                settled = true
                URL.revokeObjectURL(objectUrl)
                resolve({
                  width,
                  height,
                  data: new Uint8Array(arrayBuffer)
                })
              } catch (error) {
                fail(error)
              }
            }, 'image/jpeg', 1)
          } catch (error) {
            fail(error)
          }
        }
        image.onerror = () => fail(new Error('Image load failed'))
        image.src = objectUrl
      })
    },
    $buildDownloadPdfBlob(imagePages) {
      if (!Array.isArray(imagePages) || imagePages.length == 0) {
        throw new Error('PDF conversion unavailable')
      }
      const encoder = new TextEncoder()
      const chunks = []
      const offsets = []
      let offset = 0
      const writeString = value => {
        const bytes = encoder.encode(value)
        chunks.push(bytes)
        offset += bytes.length
      }
      const writeBytes = bytes => {
        chunks.push(bytes)
        offset += bytes.length
      }
      const objectCount = 2 + imagePages.length * 3
      const writeObject = (id, parts) => {
        offsets[id] = offset
        writeString(`${id} 0 obj\n`)
        parts.forEach(part => {
          if (typeof part == 'string') {
            writeString(part)
          } else {
            writeBytes(part)
          }
        })
        writeString('\nendobj\n')
      }
      writeString('%PDF-1.4\n')
      writeObject(1, ['<< /Type /Catalog /Pages 2 0 R >>'])
      const pageIds = imagePages.map((page, index) => 3 + index * 3)
      writeObject(2, [`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`])
      imagePages.forEach((page, index) => {
        const pageId = 3 + index * 3
        const contentId = pageId + 1
        const imageId = pageId + 2
        const layout = this.$getDownloadPdfPageLayout(page)
        const imageName = `Im${index + 1}`
        const content = `q\n${layout.drawWidth} 0 0 ${layout.drawHeight} ${layout.offsetX} ${layout.offsetY} cm\n/${imageName} Do\nQ`
        writeObject(pageId, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${layout.pageWidth} ${layout.pageHeight}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`])
        writeObject(contentId, [`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`])
        writeObject(imageId, [
          `<< /Type /XObject /Subtype /Image /Width ${layout.imageWidth} /Height ${layout.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
          page.data,
          '\nendstream'
        ])
      })
      const xrefOffset = offset
      writeString(`xref\n0 ${objectCount + 1}\n`)
      writeString('0000000000 65535 f \n')
      for (let id = 1; id <= objectCount; id++) {
        writeString(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`)
      }
      writeString(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`)
      return new Blob(chunks, { type: 'application/pdf' })
    },
    $compareDownloadEntryNames(leftName, rightName) {
      const left = String(leftName || '')
      const right = String(rightName || '')
      try {
        return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' })
      } catch (error) {
        return left.localeCompare(right)
      }
    },
    $getDownloadZipEntries(zip, matcher) {
      return Object.keys(zip && zip.files || {})
        .map(name => zip.files[name])
        .filter(file => file && !file.dir && (!matcher || matcher(file.name || '')))
        .sort((left, right) => this.$compareDownloadEntryNames(left.name, right.name))
    },
    async $convertDownloadZipBlobToPdf(blob) {
      const zip = await JSZip.loadAsync(blob)
      const imageFiles = this.$getDownloadZipEntries(zip, name => this.$isDownloadImageBlob(name, null))
      if (imageFiles.length > 0) {
        const imagePages = []
        for (const file of imageFiles) {
          const imageBlob = await file.async('blob')
          imagePages.push(await this.$downloadImageBlobToPdfPage(imageBlob, file.name))
        }
        return this.$buildDownloadPdfBlob(imagePages)
      }
      const pdfFiles = this.$getDownloadZipEntries(zip, name => this.$isDownloadPdfBlob(name, null))
      if (pdfFiles.length == 1) {
        return this.$convertDownloadPdfBlobToA4(await pdfFiles[0].async('blob'))
      }
      throw new Error('PDF conversion unavailable')
    },
    async $convertDownloadBlobToPdfEntry(blob, fileName, fallbackName = 'download') {
      const pdfFileName = this.$getDownloadFileName(fileName, 'pdf', fallbackName)
      const signature = await this.$getDownloadBlobSignature(blob)
      if (signature == 'pdf' || (!signature && this.$isDownloadPdfBlob(fileName, blob))) {
        return {
          blob: await this.$convertDownloadPdfBlobToA4(blob),
          fileName: pdfFileName
        }
      }
      if (signature == 'zip' || (!signature && this.$isDownloadZipBlob(fileName, blob))) {
        return {
          blob: await this.$convertDownloadZipBlobToPdf(blob),
          fileName: pdfFileName
        }
      }
      if (this.$isDownloadImageSignature(signature) || (!signature && this.$isDownloadImageBlob(fileName, blob))) {
        const imageBlob = this.$isDownloadImageSignature(signature) && !this.$getDownloadBlobMimeType(blob).startsWith('image/')
          ? new Blob([blob], { type: signature })
          : blob
        const page = await this.$downloadImageBlobToPdfPage(imageBlob, fileName)
        return {
          blob: this.$buildDownloadPdfBlob([page]),
          fileName: pdfFileName
        }
      }
      throw new Error('PDF conversion unavailable')
    },
    async $prepareDownloadBundleEntry(runtimeTask, itemFormat, selectedFormat, index) {
      const fallbackName = `file-${index + 1}`
      const fileName = runtimeTask.capturedFileName || runtimeTask.fileName || `${fallbackName}.bin`
      const blob = runtimeTask.capturedBlob
      if (selectedFormat == 'pdf') {
        return this.$convertDownloadBlobToPdfEntry(blob, fileName, fallbackName)
      }
      return { blob, fileName }
    },
    $isDownloadBundlePdfEntry(fileName, blob) {
      return this.$isDownloadPdfBlob(fileName, blob)
    },
    $getDownloadBundleEntryOptions(fileName, blob, itemFormat, selectedFormat) {
      if (this.$isDownloadBundlePdfEntry(fileName, blob, itemFormat, selectedFormat)) {
        return { compression: 'STORE' }
      }
      return undefined
    },
    $getDownloadBundleZipOptions(format) {
      if (format == 'pdf') {
        return { type: 'blob', compression: 'STORE' }
      }
      return { type: 'blob' }
    },
    $getUniqueDownloadBundleEntryName(usedNames, fileName, index) {
      const fallback = `file-${index + 1}.bin`
      const safeFileName = String(fileName || fallback).replace(/[\\/:*?"<>|]/g, '_') || fallback
      const dotIndex = safeFileName.lastIndexOf('.')
      const base = dotIndex > 0 ? safeFileName.slice(0, dotIndex) : safeFileName
      const extension = dotIndex > 0 ? safeFileName.slice(dotIndex) : ''
      let candidate = safeFileName
      let counter = 2
      while (usedNames.has(candidate.toLowerCase())) {
        candidate = `${base} (${counter})${extension}`
        counter += 1
      }
      usedNames.add(candidate.toLowerCase())
      return candidate
    },
    $prepareDownloadBundleItemTask(task, itemTask, bundleState, progressContext = {}) {
      const runtimeTask = Object.assign({}, itemTask || {})
      const preparedDetail = itemTask && itemTask.detail ? itemTask.detail : null
      const preparedPages = itemTask && Array.isArray(itemTask.pages) ? itemTask.pages.slice() : []
      const context = {
        index: Math.max(0, Number(progressContext.index) || 0),
        total: Math.max(1, Number(progressContext.total) || 1)
      }
      const itemRecord = progressContext.itemRecord || null
      let runtimeCancelled = !!runtimeTask.cancelled
      let runtimeAbortController = runtimeTask.abortController || null
      Object.defineProperty(runtimeTask, '__downloadBundleParentTask', {
        value: task,
        configurable: true,
        enumerable: false
      })
      Object.defineProperty(runtimeTask, '__downloadBundleProgressContext', {
        value: context,
        configurable: true,
        enumerable: false,
        writable: true
      })
      Object.defineProperty(runtimeTask, '__downloadBundleItemRecord', {
        value: itemRecord,
        configurable: true,
        enumerable: false,
        writable: true
      })
      Object.defineProperty(runtimeTask, 'cancelled', {
        configurable: true,
        enumerable: true,
        get: () => !!(runtimeCancelled || task && task.cancelled),
        set: value => {
          runtimeCancelled = !!value
          if (task && task.cancelled != runtimeCancelled) {
            this.$set(task, 'cancelled', runtimeCancelled)
          }
        }
      })
      Object.defineProperty(runtimeTask, 'abortController', {
        configurable: true,
        enumerable: true,
        get: () => runtimeAbortController,
        set: value => {
          runtimeAbortController = value || null
          if (task && task.abortController !== runtimeAbortController) {
            this.$set(task, 'abortController', runtimeAbortController)
          }
        }
      })
      runtimeTask.id = bundleState.id
      runtimeTask.bundleFileName = bundleState.fileName
      runtimeTask.bundleName = bundleState.name
      runtimeTask.sourceItems = bundleState.items
      runtimeTask.captureBlob = true
      runtimeTask.capturedBlob = null
      runtimeTask.capturedFileName = ''
      runtimeTask.delegatedDownloadInitialized = false
      runtimeTask.detail = preparedDetail
      runtimeTask.pages = preparedPages
      runtimeTask.pngFiles = []
      runtimeTask.pdfPages = []
      runtimeTask.pdfPageGroups = []
      runtimeTask.failedPageIndex = 0
      runtimeTask.pdfDocument = null
      runtimeTask.pdfPageCount = 0
      runtimeTask.openUrl = ''
      runtimeTask.state = 'running'
      return runtimeTask
    },
    $syncDownloadBundleItemTaskPatch(itemTask, patch) {
      const parentTask = itemTask && itemTask.__downloadBundleParentTask
      this.$syncDownloadBundleItemRecordPatch(itemTask, patch)
      if (!parentTask || !patch) {
        return
      }
      const context = itemTask.__downloadBundleProgressContext || {}
      const parentPatch = {}
      if (Object.prototype.hasOwnProperty.call(patch, 'progress')) {
        const nextProgress = this.$getDownloadBundleItemProgress(context.index, context.total, patch.progress)
        parentPatch.progress = Math.max(Number(parentTask.progress) || 0, nextProgress)
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'state')) {
        parentPatch.state = patch.state == 'done' || patch.state == 'error' ? 'running' : patch.state
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'statusText')) {
        parentPatch.statusText = this.$getDownloadBundleItemStatusText(context)
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'abortController')) {
        parentPatch.abortController = patch.abortController || null
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'cancelled') && patch.cancelled) {
        parentPatch.cancelled = true
      }
      if (Object.keys(parentPatch).length > 0 && typeof this.updatePaperDownloadTask == 'function') {
        this.updatePaperDownloadTask(parentTask, parentPatch)
      }
    },
    $normalizePaperDownloadTaskPatch(task, patch) {
      if (patch && Object.prototype.hasOwnProperty.call(patch, 'statusText')) {
        const nextState = Object.prototype.hasOwnProperty.call(patch, 'state')
          ? patch.state
          : (task && task.state)
        if (nextState == 'running') {
          const isBundleTask = this.$isDownloadBundleTask(task)
          patch = Object.assign({}, patch, {
            statusText: isBundleTask && patch.statusText
              ? patch.statusText
              : this.$getDownloadRunningStatusText()
          })
        }
      }
      if (task && task.__downloadBundleItemRecord && !task.__downloadBundleParentTask) {
        this.$syncDownloadBundleItemRecordPatch(task, patch)
      }
      if (task && task.__downloadBundleParentTask) {
        this.$syncDownloadBundleItemTaskPatch(task, patch)
      }
      return patch
    },
    async $runDownloadBundleTask(task, options = {}) {
      const items = Array.isArray(options.items) ? options.items.filter(Boolean) : (Array.isArray(task.sourceItems) ? task.sourceItems.filter(Boolean) : [])
      const format = options.format || task.format
      const password = options.password === undefined ? (task.password || '') : options.password
      const bundleState = {
        id: task.id,
        kind: options.kind || task.kind || 'download-bundle',
        fileName: task.bundleFileName || this.$getDownloadBundleFileName(items, options),
        name: task.bundleName || this.$stripDownloadFileExtension(task.bundleFileName || this.$getDownloadBundleFileName(items, options)),
        items: items.slice()
      }
      const zip = new JSZip()
      const usedNames = new Set()
      const bundleItemRecords = this.$createDownloadBundleItemRecords(task, items, options)
      let bundledItemCount = 0
      let skippedEmptyAnnotationCount = 0
      const failedItems = []
      try {
        if (items.length == 0) {
          throw new Error('No downloadable file')
        }
        this.$registerDownloadBundleItemRetryHandler(task, options)
        this.$set(task, 'bundleItems', bundleItemRecords)
        this.$set(task, 'bundleCompletedCount', 0)
        this.$set(task, 'bundleFailedCount', 0)
        this.$set(task, 'bundleTotalCount', items.length)
        this.$set(task, 'bundleProgressCurrent', 0)
        this.$set(task, 'bundleProgressTotal', items.length)
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          const itemRecord = bundleItemRecords[i]
          let itemTask = null
          let itemRuntimeTask = null
          let itemFormat = format
          let itemName = this.$getDownloadBundleItemDisplayName(item, null, i, options)
          try {
            if (typeof this.ensurePaperDownloadTaskActive == 'function') {
              this.ensurePaperDownloadTaskActive(task)
            }
            itemFormat = typeof options.getItemFormat == 'function' ? options.getItemFormat(item, format) : format
            itemTask = typeof options.createItemTask == 'function'
              ? options.createItemTask(item, itemFormat, password)
              : null
            itemName = this.$getDownloadBundleItemDisplayName(item, itemTask, i, options)
            this.$updateDownloadBundleItemRecord(itemRecord, {
              name: itemName,
              fileName: itemTask && itemTask.fileName ? itemTask.fileName : '',
              progress: 0,
              state: 'running',
              statusText: this.$getDownloadBundleRecordStatusText('running'),
              failureReason: ''
            })
            if (!itemTask || typeof options.runItemTask != 'function') {
              throw new Error('Download unavailable')
            }
            itemRuntimeTask = this.$prepareDownloadBundleItemTask(task, itemTask, bundleState, {
              index: i,
              total: items.length,
              itemRecord
            })
            this.$set(task, 'bundleProgressCurrent', Math.min(i + 1, items.length))
            this.$set(task, 'bundleProgressTotal', items.length)
            this.updatePaperDownloadTask(task, {
              fileName: bundleState.fileName,
              name: bundleState.name,
              state: 'running',
              progress: this.$getDownloadBundleItemProgress(i, items.length, 0),
              statusText: this.$getDownloadBundleItemStatusText({ index: i, total: items.length })
            })
            await options.runItemTask(itemRuntimeTask, item, itemFormat)
            this.$set(task, 'kind', bundleState.kind)
            this.$set(task, 'sourceItems', bundleState.items)
            this.$set(task, 'format', format)
            this.$set(task, 'password', password)
            this.$set(task, 'bundleFileName', bundleState.fileName)
            this.$set(task, 'bundleName', bundleState.name)
            if (task.cancelled || itemRuntimeTask.cancelled) {
              const cancelError = new Error('Download cancelled')
              cancelError.isPaperDownloadCancelled = true
              throw cancelError
            }
            if (itemRuntimeTask.state == 'error') {
              throw new Error(itemRuntimeTask.statusText || this.$getDownloadText('downloadFailed', '下载失败'))
            }
            if (!itemRuntimeTask.capturedBlob) {
              throw new Error(this.$getDownloadText('downloadFailed', '下载失败'))
            }
            const bundleEntry = await this.$prepareDownloadBundleEntry(itemRuntimeTask, itemFormat, format, i)
            const entryName = this.$getUniqueDownloadBundleEntryName(usedNames, bundleEntry.fileName, i)
            const entryOptions = this.$getDownloadBundleEntryOptions(entryName, bundleEntry.blob, itemFormat, format)
            if (entryOptions) {
              zip.file(entryName, bundleEntry.blob, entryOptions)
            } else {
              zip.file(entryName, bundleEntry.blob)
            }
            bundledItemCount += 1
            this.$updateDownloadBundleItemRecord(itemRecord, {
              fileName: entryName,
              progress: 100,
              state: 'done',
              statusText: this.$getDownloadBundleRecordStatusText('done'),
              failureReason: '',
              abortController: null
            })
            this.$set(task, 'bundleCompletedCount', bundledItemCount)
            this.$set(task, 'bundleProgressCurrent', Math.min(i + 1, items.length))
            this.$set(task, 'bundleProgressTotal', items.length)
            this.$set(task, 'kind', bundleState.kind)
            this.$set(task, 'sourceItems', bundleState.items)
            this.$set(task, 'format', format)
            this.$set(task, 'password', password)
            this.$set(task, 'bundleFileName', bundleState.fileName)
            this.$set(task, 'bundleName', bundleState.name)
          } catch (itemError) {
            if (itemError && itemError.isPaperDownloadCancelled) {
              throw itemError
            }
            if (task.cancelled || itemRuntimeTask && itemRuntimeTask.cancelled) {
              const cancelError = new Error('Download cancelled')
              cancelError.isPaperDownloadCancelled = true
              throw cancelError
            }
            if (this.$isLearningAnnotationsEmptyDownloadError(itemError)) {
              skippedEmptyAnnotationCount += 1
            }
            const failureReason = this.$getDownloadBundleFailureReason(itemError)
            const failureContext = { item, itemTask: itemRuntimeTask, itemRecord, index: i }
            failedItems.push({ name: itemName, error: itemError, failureReason })
            this.$updateDownloadBundleItemRecord(itemRecord, {
              progress: Math.max(0, Math.min(Math.round(Number(itemRecord && itemRecord.progress) || 0), 99)),
              state: 'error',
              statusText: this.$getDownloadBundleItemFailureStatusText(itemError, options, failureContext),
              failureReason,
              abortController: null
            })
            this.$set(task, 'bundleFailedCount', failedItems.length)
            this.$set(task, 'bundleProgressCurrent', Math.min(i + 1, items.length))
            this.$set(task, 'bundleProgressTotal', items.length)
            if (this.$shouldNotifyDownloadBundleItemFailure(itemError, options, failureContext)) {
              this.$notifyDownloadBundleItemFailure(itemName)
            }
          }
          this.updatePaperDownloadTask(task, {
            fileName: bundleState.fileName,
            name: bundleState.name,
            state: 'running',
            progress: this.$getDownloadBundleItemProgress(i, items.length, 100),
            statusText: this.$getDownloadBundleItemStatusText({ index: i, total: items.length })
          })
        }
        this.$set(task, 'captureBlob', false)
        this.$set(task, 'capturedBlob', null)
        this.$set(task, 'capturedFileName', '')
        this.$set(task, 'abortController', null)
        if (bundledItemCount == 0 && skippedEmptyAnnotationCount > 0 && failedItems.length == skippedEmptyAnnotationCount) {
          this.$handleLearningAnnotationsEmptyDownloadTask(task)
          return
        }
        if (bundledItemCount == 0 && failedItems.length > 0) {
          throw new Error(this.$getDownloadText('downloadFailed', '下载失败'))
        }
        const finalProgress = this.$getDownloadBundleCountProgress(bundledItemCount, items.length)
        const finalStatusText = this.$getDownloadBundleDoneStatusText(bundledItemCount, items.length)
        this.$set(task, 'bundleCompletedCount', bundledItemCount)
        this.$set(task, 'bundleFailedCount', failedItems.length)
        this.$set(task, 'bundleTotalCount', items.length)
        this.$set(task, 'bundleProgressCurrent', bundledItemCount)
        this.$set(task, 'bundleProgressTotal', items.length)
        this.updatePaperDownloadTask(task, {
          fileName: bundleState.fileName,
          name: bundleState.name,
          progress: finalProgress,
          statusText: finalStatusText
        })
        const zipBlob = await zip.generateAsync(this.$getDownloadBundleZipOptions(format), () => {
          this.updatePaperDownloadTask(task, {
            progress: finalProgress,
            statusText: finalStatusText
          })
        })
        if (typeof this.ensurePaperDownloadTaskActive == 'function') {
          this.ensurePaperDownloadTaskActive(task)
        }
        if (typeof options.saveBundleBlob == 'function') {
          options.saveBundleBlob(task, zipBlob)
        } else if (typeof this.savePaperDownloadBlob == 'function') {
          this.savePaperDownloadBlob(task, zipBlob)
        }
        this.updatePaperDownloadTask(task, {
          progress: finalProgress,
          state: 'done',
          statusText: finalStatusText
        })
      } catch (error) {
        this.$set(task, 'captureBlob', false)
        this.$set(task, 'abortController', null)
        this.$set(task, 'kind', bundleState.kind)
        this.$set(task, 'sourceItems', bundleState.items)
        this.$set(task, 'bundleFileName', bundleState.fileName)
        this.$set(task, 'bundleName', bundleState.name)
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = typeof this.isLikelyCorsDownloadError == 'function' && this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: this.$getDownloadBundleCountProgress(bundledItemCount, items.length),
          statusText: isCorsLikeError ? this.$getDownloadText('imageReadFailed', '图片读取失败，请点击重试') : (error && error.message ? error.message : this.$getDownloadText('downloadFailed', '下载失败'))
        })
        this.$set(task, 'bundleCompletedCount', bundledItemCount)
        this.$set(task, 'bundleFailedCount', failedItems.length)
        this.$set(task, 'bundleTotalCount', items.length)
        this.$set(task, 'bundleProgressCurrent', bundledItemCount)
        this.$set(task, 'bundleProgressTotal', items.length)
      }
    },
    $hasRunningDownloadTasks() {
      return this.paperDownloadTasks.some(task => task && task.state == 'running')
    },
    $enqueuePaperDownloadTask(task, runner) {
      if (!this.$canStartDownloadBatch(1, 1)) {
        return false
      }
      if (task && task.state == 'running') {
        task.statusText = this.$getDownloadRunningStatusText()
      }
      downloadCenter.addTask(task, {
        retry: () => {
          if (typeof runner == 'function') {
            runner()
          }
        },
        revoke: item => {
          if (typeof this.revokePaperDownloadTaskUrl == 'function') {
            this.revokePaperDownloadTaskUrl(item)
          } else {
            downloadCenter.revokeTaskUrl(item)
          }
        }
      })
      if (typeof runner == 'function') {
        runner()
      }
      return true
    }
  }
}
